import { useEffect, useId, useReducer, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/Button";
import { useTranslations } from "@/lib/useTranslations";
import {
  type CalendarDay,
  type DateRangePresetId,
  type RangeSegment,
  formatMonthYear,
  formatRangeSummary,
  formatShortDisplayDate,
  getCalendarMonthDays,
  getDateRangePreset,
  getRangeSegment,
  normalizeDateRange,
  parseDateInput,
  toDateInput,
} from "@/lib/dateRange";

const PRESET_IDS: DateRangePresetId[] = [
  "all",
  "today",
  "last7Days",
  "lastMonth",
  "quarter",
  "sixMonths",
  "year",
];

const WEEKDAY_LABELS = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"] as const;

interface DateRangePickerProps {
  id: string;
  label: string;
  from: string | null;
  to: string | null;
  allDatesLabel: string;
  onApply: (from: string | null, to: string | null) => void;
}

function ChevronLeftIcon() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" fill="none" className="size-4">
      <path
        d="M10 3L5 8l5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" fill="none" className="size-4">
      <path
        d="M6 3l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type DraftRange = { from: string | null; to: string | null };

type DraftRangeAction =
  | { type: "sync"; from: string | null; to: string | null }
  | { type: "preset"; from: string | null; to: string | null }
  | { type: "selectDay"; dateInput: string };

function draftRangeReducer(
  state: DraftRange,
  action: DraftRangeAction,
): DraftRange {
  switch (action.type) {
    case "sync":
    case "preset":
      return { from: action.from, to: action.to };
    case "selectDay": {
      if (!state.from || (state.from && state.to)) {
        return { from: action.dateInput, to: null };
      }
      return normalizeDateRange(state.from, action.dateInput);
    }
  }
}

function getInitialMonth(from: string | null, to: string | null): Date {
  const anchor = from ?? to;
  if (anchor) return parseDateInput(anchor);
  return new Date();
}

function formatPresetSubtitle(
  preset: DateRangePresetId,
  allDatesLabel: string,
): string {
  const range = getDateRangePreset(preset);
  if (!range.from && !range.to) return allDatesLabel;
  if (range.from && range.to) {
    if (range.from === range.to) return formatShortDisplayDate(range.from);
    return `${formatShortDisplayDate(range.from)} – ${formatShortDisplayDate(range.to)}`;
  }
  return allDatesLabel;
}

function getRangeTrackClassName(segment: RangeSegment): string {
  switch (segment) {
    case "start":
      return "absolute top-1/2 left-1/2 h-7 w-1/2 -translate-y-1/2 rounded-l-full bg-yellow-100";
    case "middle":
      return "absolute top-1/2 right-0 left-0 h-7 -translate-y-1/2 bg-yellow-100";
    case "end":
      return "absolute top-1/2 left-0 h-7 w-1/2 -translate-y-1/2 rounded-r-full bg-yellow-100";
    default:
      return "";
  }
}

function CalendarDayButton({
  day,
  draftFrom,
  draftTo,
  onSelect,
}: {
  day: CalendarDay;
  draftFrom: string | null;
  draftTo: string | null;
  onSelect: (dateInput: string) => void;
}) {
  const segment = getRangeSegment(day.dateInput, draftFrom, draftTo);
  const isEndpoint =
    segment === "single" || segment === "start" || segment === "end";
  const trackClassName = getRangeTrackClassName(segment);

  return (
    <div className="relative h-8 overflow-hidden">
      {trackClassName ? (
        <div aria-hidden className={`pointer-events-none ${trackClassName}`} />
      ) : null}
      <button
        type="button"
        onClick={() => onSelect(day.dateInput)}
        className={`relative z-10 mx-auto flex size-7 items-center justify-center rounded-full text-sm transition-colors ${
          isEndpoint
            ? "bg-zinc-900 font-medium text-white"
            : day.inCurrentMonth
              ? "text-whart-text hover:bg-zinc-100"
              : "text-neutral-300 hover:bg-zinc-50"
        }`}
        aria-label={toDateInput(day.date)}
        aria-pressed={isEndpoint}
      >
        {day.date.getDate()}
      </button>
    </div>
  );
}

export function DateRangePicker({
  id,
  label,
  from,
  to,
  allDatesLabel,
  onApply,
}: DateRangePickerProps) {
  const t = useTranslations("events.library");
  const tCommon = useTranslations("common");
  const titleId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [draftRange, dispatchDraftRange] = useReducer(draftRangeReducer, {
    from,
    to,
  });
  const { from: draftFrom, to: draftTo } = draftRange;
  const [activePreset, setActivePreset] = useState<DateRangePresetId | null>(
    null,
  );
  const [visibleMonth, setVisibleMonth] = useState(() =>
    getInitialMonth(from, to),
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  function openPicker() {
    dispatchDraftRange({ type: "sync", from, to });
    setActivePreset(null);
    setVisibleMonth(getInitialMonth(from, to));
    setIsOpen(true);
  }

  function handleCancel() {
    setIsOpen(false);
  }

  function handleApply() {
    const effectiveFrom = draftFrom ?? draftTo;
    const effectiveTo = draftTo ?? draftFrom;
    const normalized = normalizeDateRange(effectiveFrom, effectiveTo);
    onApply(normalized.from, normalized.to);
    setIsOpen(false);
  }

  function handlePresetSelect(preset: DateRangePresetId) {
    const range = getDateRangePreset(preset);
    dispatchDraftRange({ type: "preset", ...range });
    setActivePreset(preset);

    const anchor = range.from ?? range.to;
    if (anchor) {
      setVisibleMonth(parseDateInput(anchor));
    }
  }

  function handleDaySelect(dateInput: string) {
    setActivePreset(null);
    dispatchDraftRange({ type: "selectDay", dateInput });
  }

  function goToPreviousMonth() {
    setVisibleMonth(
      (current) => new Date(current.getFullYear(), current.getMonth() - 1, 1),
    );
  }

  function goToNextMonth() {
    setVisibleMonth(
      (current) => new Date(current.getFullYear(), current.getMonth() + 1, 1),
    );
  }

  const monthDays = getCalendarMonthDays(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth(),
  );

  const panel = isOpen ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label={tCommon("closeDialog")}
        className="absolute inset-0 bg-black/20"
        onClick={handleCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 flex w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm sm:flex-row"
      >
        <aside className="border-b border-black/10 bg-zinc-50 p-3 sm:w-56 sm:border-r sm:border-b-0">
          <p className="px-2 pb-2 text-xs font-medium uppercase tracking-wider text-neutral-500">
            {label}
          </p>
          <ul className="space-y-1">
            {PRESET_IDS.map((preset) => {
              const isActive = activePreset === preset;

              return (
                <li key={preset}>
                  <button
                    type="button"
                    onClick={() => handlePresetSelect(preset)}
                    className={`w-full rounded-xl px-3 py-2 text-left transition-colors ${
                      isActive
                        ? "bg-yellow-100 text-zinc-900"
                        : "text-whart-text hover:bg-white"
                    }`}
                  >
                    <span className="block text-sm font-medium">
                      {t(`filtersDatePresets.${preset}`)}
                    </span>
                    <span className="block text-xs text-neutral-500">
                      {formatPresetSubtitle(preset, allDatesLabel)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 id={titleId} className="text-base font-semibold text-whart-text">
                {formatMonthYear(
                  visibleMonth.getFullYear(),
                  visibleMonth.getMonth(),
                )}
              </h3>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="inline-flex size-8 items-center justify-center rounded-xl text-neutral-600 transition-colors hover:bg-zinc-50"
                  aria-label={t("filtersDatePickerPreviousMonth")}
                >
                  <ChevronLeftIcon />
                </button>
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="inline-flex size-8 items-center justify-center rounded-xl text-neutral-600 transition-colors hover:bg-zinc-50"
                  aria-label={t("filtersDatePickerNextMonth")}
                >
                  <ChevronRightIcon />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7">
              {WEEKDAY_LABELS.map((weekday) => (
                <div
                  key={weekday}
                  className="flex h-7 items-center justify-center text-xs font-medium text-neutral-500"
                >
                  {weekday}
                </div>
              ))}
              {monthDays.map((day) => (
                <CalendarDayButton
                  key={day.dateInput}
                  day={day}
                  draftFrom={draftFrom}
                  draftTo={draftTo}
                  onSelect={handleDaySelect}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-black/10 px-4 py-3">
            <Button type="button" variant="ghost" onClick={handleCancel}>
              {tCommon("cancel")}
            </Button>
            <Button type="button" onClick={handleApply}>
              {t("filtersDatePickerApply")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="space-y-1.5">
      <span
        id={`${id}-label`}
        className="text-xs font-medium uppercase tracking-wider text-neutral-500"
      >
        {label}
      </span>
      <button
        id={id}
        type="button"
        aria-labelledby={`${id}-label`}
        onClick={openPicker}
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-black/10 bg-white px-3 py-2.5 text-left text-sm text-whart-text shadow-sm transition-colors hover:bg-zinc-50"
      >
        <span>{formatRangeSummary(from, to, allDatesLabel)}</span>
        <svg
          aria-hidden
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="size-4 shrink-0 text-neutral-500"
        >
          <path d="M5 2v2M11 2v2M3 6h10M4 4h8a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
        </svg>
      </button>
      {panel ? createPortal(panel, document.body) : null}
    </div>
  );
}
